import { useCallback, useEffect, useMemo } from "react";
import { api, type ApiRequestOptions } from "./api.ts";

/** Cancel a page's requests on unmount, or a detail's requests on selection change. */
export function useScopedApi(scopeKey = "page"): typeof api {
  const scope = useMemo(
    () => ({ controller: new AbortController() }),
    [scopeKey],
  );
  useEffect(() => {
    // React StrictMode replays effect setup after cleanup in development.
    if (scope.controller.signal.aborted)
      scope.controller = new AbortController();
    return () => scope.controller.abort();
  }, [scope]);

  return useCallback(
    async <T>(path: string, input: ApiRequestOptions = {}): Promise<T> => {
      const controller = new AbortController();
      const cancel = () => controller.abort();
      const pageSignal = scope.controller.signal;
      const signals =
        input.signal === undefined ? [pageSignal] : [pageSignal, input.signal];
      for (const signal of signals) {
        signal.addEventListener("abort", cancel, { once: true });
        if (signal.aborted) cancel();
      }
      try {
        return await api<T>(path, { ...input, signal: controller.signal });
      } finally {
        for (const signal of signals)
          signal.removeEventListener("abort", cancel);
      }
    },
    [scope],
  );
}
